import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
        setLoading(false);
      } catch (err) {
        setError('Blog post not found');
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h1 className="text-4xl font-bold mb-4">Blog Post Not Found</h1>
          <p className="text-muted-foreground mb-8">The blog post you are looking for does not exist.</p>
          <Link to="/blog" className="inline-flex items-center text-primary hover:underline">
            <ArrowLeft className="mr-2" size={18} />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-detail-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Link to="/blog" className="inline-flex items-center text-primary mb-8 hover:underline">
          <ArrowLeft className="mr-2" size={18} />
          Back to Blog
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="px-4 py-1 border border-primary/30 text-primary text-xs font-mono uppercase tracking-wider">
              {post.category}
            </span>
          </div>

          <h1 data-testid="blog-post-title" className="text-4xl md:text-5xl font-black tracking-tight mb-6">
            {post.title}
          </h1>

          <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8 pb-8 border-b border-white/10">
            <span className="flex items-center">
              <User size={16} className="mr-2" />
              {post.author}
            </span>
            <span className="flex items-center">
              <Calendar size={16} className="mr-2" />
              {new Date(post.published_at).toLocaleDateString()}
            </span>
          </div>

          <div 
            data-testid="blog-post-content"
            className="prose prose-invert prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </motion.article>

        <div className="mt-12 pt-8 border-t border-white/10">
          <Link 
            to="/blog" 
            className="inline-flex items-center px-6 py-3 border border-primary/50 text-primary font-mono uppercase text-sm hover:bg-primary/10 transition-colors"
          >
            <ArrowLeft className="mr-2" size={18} />
            View All Posts
          </Link>
        </div>
      </div>
    </div>
  );
}
